import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { cartActions } from '@/features/Cart/services/cartService';
import { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import { useModalStore } from '@/features/UserInterface/stores/modal';
import { useCartMaxQuantity } from '@/features/Cart/composables/useCartMaxQuantity';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { gtmEvent } from '@/composables/gtm/useGtm';
import { useCartStore } from '@/features/Cart/stores/cart';
import type {
  ProductComplete,
  lockedToBundle,
  AllowedAtcButtonVariants,
  AtcButtonTag,
} from '../types/models';
import type { ButtonOptions } from '@/features/PageSection/types/models';
import type { AddCartOutcome, AtcObjectRedraw, CartActionPlan } from '@/features/Cart/types/models';

export type LoadingState = false | 'adding' | 'added' | 'error';
type AtcButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export type AtcButtonProps = Omit<Partial<ButtonOptions>, 'loading' | 'size' | 'to'> & {
  size?: AtcButtonSize;
  loading?: LoadingState | boolean;
  loadingText?: string;
  to?: RouteLocationRaw;
  style?: Record<string, string | undefined>;
};

export type AtcButtonMode = 'locked' | 'free-gift' | 'bundle-link' | 'atc';

export type AtcResult = {
  outcome: AddCartOutcome;
  addedQuantity: number;
  isStockRejection: boolean;
};

export type UseProductAtcOptionalProps = {
  soldOut?: MaybeRefOrGetter<boolean>;
  sku?: MaybeRefOrGetter<string | undefined>;
  isBundle?: MaybeRefOrGetter<boolean>;
  disableCartFunctions?: MaybeRefOrGetter<boolean>;
  buttonStyle?: MaybeRefOrGetter<Record<string, string> | undefined>;
  lockedTo?: MaybeRefOrGetter<lockedToBundle | undefined>;
  onLockChange?: (locked: boolean) => void;
  size?: MaybeRefOrGetter<AtcButtonSize | undefined>;
  buttonPropsOverride?: MaybeRefOrGetter<Partial<ButtonOptions> | undefined>;
  bundleLinkTo?: MaybeRefOrGetter<RouteLocationRaw | undefined>;
  useStyleTagVariant?: MaybeRefOrGetter<boolean>;
  redirectTo?: MaybeRefOrGetter<RouteLocationRaw | false | undefined>;
  actionPlan?: MaybeRefOrGetter<CartActionPlan | undefined>;
  beforeAdd?: MaybeRefOrGetter<(() => Promise<boolean>) | undefined>;
  freeGiftSku?: MaybeRefOrGetter<string | undefined>;
};

export type UseProductAtcOptions = {
  product: MaybeRefOrGetter<ProductComplete>;
  quantity: MaybeRefOrGetter<number>;
  productPrice: MaybeRefOrGetter<number>;
  atcObject: MaybeRefOrGetter<AddToCartItem[]>;
  options?: UseProductAtcOptionalProps;
  transformAtcObject?: MaybeRefOrGetter<((items: AddToCartItem[]) => AddToCartItem[]) | undefined>;
  redrawAtcObject?: MaybeRefOrGetter<AtcObjectRedraw | undefined>;
};

export const useProductAtc = (params: UseProductAtcOptions) => {
  const options = { ...params, ...params.options };
  const { openModal } = useModalStore();
  const { refreshStock } = useProductStockStore();
  const { addCartItems } = cartActions();
  const shouldUseRouter = toValue(options.redirectTo) !== false;
  const router = shouldUseRouter ? useRouter() : null;

  const primaryAtcItem = computed(() => {
    const items = toValue(options.atcObject);
    const variantId = toValue(options.product)?.attributes.variantId;
    return items.find((item) => item.id === variantId) ?? items[0];
  });

  const {
    maxCartQuantity: maxAllowed,
    remainingCartQuantity: allowedQuantity,
    stockAddableQuantity,
    clampToMaxCartQuantity,
  } = useCartMaxQuantity({
    product: options.product,
    stockItem: () =>
      primaryAtcItem.value && {
        variantId: primaryAtcItem.value.id,
        properties: primaryAtcItem.value.properties,
      },
  });
  const clampedQuantity = computed(() => {
    const requestedQuantity = toValue(options.quantity);
    if (toValue(options.isBundle)) {
      return Math.min(
        Math.max(0, Math.floor(requestedQuantity)),
        stockAddableQuantity.value ?? Infinity,
      );
    }
    return clampToMaxCartQuantity(requestedQuantity);
  });

  const resolvedAtcObject = computed<AddToCartItem[]>(() => {
    const product = toValue(options.product);
    const items = toValue(options.atcObject);
    if (!product || !items.length || clampedQuantity.value <= 0) return [];
    const primaryItemIndex = items.findIndex((item) => item.id === product.attributes.variantId);
    const indexToClamp = primaryItemIndex >= 0 ? primaryItemIndex : 0;
    const clampedItems = items.map((item, index) => ({
      ...item,
      quantity: index === indexToClamp ? clampedQuantity.value : item.quantity,
    }));
    const transformAtcObject = toValue(options.transformAtcObject);
    return transformAtcObject ? transformAtcObject(clampedItems) : clampedItems;
  });

  const buttonAtcState = ref<LoadingState>(false);
  const buttonLoadingText = ref<string>();

  const { getFreeGiftProductButton } = useCartStore();

  const freeGiftProductButton = computed(() =>
    getFreeGiftProductButton(toValue(options.product)?.attributes.sku),
  );

  const mode = computed<AtcButtonMode>(() => {
    if (toValue(options.lockedTo)) return 'locked';
    if (freeGiftProductButton.value) return 'free-gift';
    if (toValue(options.bundleLinkTo)) return 'bundle-link';
    return 'atc';
  });

  const atcButtonText = computed(() => {
    const product = toValue(options.product);
    if (product?.content?.customButtonText) return product.content.customButtonText;
    return toValue(options.isBundle) ? 'Add bundle to cart' : 'Add to cart';
  });

  const atcButtonVariant = computed<AllowedAtcButtonVariants>(() => {
    if (toValue(options.useStyleTagVariant) ?? true) {
      const tag = toValue(options.product)?.content?.styleTags?.find((t): t is AtcButtonTag =>
        t.endsWith('-atc-button'),
      );
      if (tag) {
        return tag.replace('-atc-button', '') as AllowedAtcButtonVariants;
      }
    }
    if (toValue(options.isBundle)) return 'blue';
    return 'primary';
  });

  const showAtcError = (text: string) => {
    buttonAtcState.value = 'error';
    buttonLoadingText.value = text;
    options.onLockChange?.(false);
    setTimeout(() => {
      buttonAtcState.value = false;
      buttonLoadingText.value = undefined;
    }, 2000);
  };

  const handleAddToCart = async (): Promise<AtcResult> => {
    const notAdded: AtcResult = { outcome: 'rejected', addedQuantity: 0, isStockRejection: false };
    const soldOut = toValue(options.soldOut);
    const sku = toValue(options.sku);
    if (soldOut && sku) {
      openModal(`sold-out-modal-${sku}`);
      return notAdded;
    }

    const productToAdd = toValue(options.product);
    if (!productToAdd || !toValue(options.atcObject).length || clampedQuantity.value <= 0) {
      return notAdded;
    }

    const beforeAdd = toValue(options.beforeAdd);
    if (beforeAdd && !(await beforeAdd())) return notAdded;

    if (!resolvedAtcObject.value.length) {
      buttonAtcState.value = 'adding';
      options.onLockChange?.(true);
      await refreshStock({ force: true });
    }
    const itemsToAdd = resolvedAtcObject.value;
    if (!itemsToAdd.length) {
      showAtcError('Not enough stock');
      return { outcome: 'rejected', addedQuantity: 0, isStockRejection: true };
    }

    buttonAtcState.value = 'adding';
    options.onLockChange?.(true);
    try {
      const { outcome, addedQuantities, isStockRejection } = await addCartItems(itemsToAdd, {
        actionPlan: toValue(options.actionPlan),
        redrawItems: toValue(options.redrawAtcObject),
        freeGiftSku: toValue(options.freeGiftSku),
      });
      if (outcome === 'rejected') {
        showAtcError(isStockRejection ? 'Not enough stock' : 'Error');
        return { outcome, addedQuantity: 0, isStockRejection };
      }
      if (outcome === 'unconfirmed') {
        showAtcError('Check your cart');
        return { outcome, addedQuantity: 0, isStockRejection };
      }
      const product = toValue(options.product);
      const primaryItemIndex = Math.max(
        0,
        itemsToAdd.findIndex((item) => item.id === product?.attributes.variantId),
      );
      const addedQuantity = addedQuantities[primaryItemIndex];
      if (product && addedQuantity > 0) {
        const productData = {
          sku: product.attributes.sku,
          title: product.content.title,
          price: toValue(options.productPrice),
          quantity: addedQuantity,
          id: product.attributes.variantId,
        };
        await gtmEvent().customAtc(productData);
        setTimeout(() => gtmEvent().cartInit(productData), 1000);
      }
      const isPartialAdd = outcome === 'partial';
      if (isPartialAdd) {
        const partialAddText = isStockRejection ? 'Not enough stock' : 'Check your cart';
        showAtcError(itemsToAdd.length > 1 ? partialAddText : `Only ${addedQuantity} added`);
      } else {
        buttonAtcState.value = 'added';
      }
      const redirectTo = toValue(options.redirectTo);

      if (redirectTo !== false) {
        setTimeout(
          () => {
            router?.push(redirectTo ?? '/cart');
          },
          isPartialAdd ? 1500 : 750,
        );
      }
      return { outcome, addedQuantity, isStockRejection };
    } catch (err) {
      console.error('Error adding product to cart', err);
      showAtcError('Error');
      return notAdded;
    }
  };

  const atcButtonProps = computed<AtcButtonProps>(() => {
    const size = toValue(options.size);

    switch (mode.value) {
      case 'locked': {
        const lockedTo = toValue(options.lockedTo);
        return {
          text: lockedTo?.text.button || '🔓 Unlock now 🔓',
          width: 'full-width',
          size,
          to: lockedTo?.link,
        };
      }

      case 'free-gift': {
        const productButton = freeGiftProductButton.value;
        return {
          text: productButton?.text,
          variant: 'positive',
          width: 'full-width',
          size,
          style: {
            backgroundColor: productButton?.backgroundColor,
            color: productButton?.textColor,
          },
          ...toValue(options.buttonPropsOverride),
          to: productButton?.to,
        };
      }

      case 'bundle-link':
        return {
          text: 'Customise bundle',
          variant: 'blue',
          width: 'full-width',
          size,
          ...toValue(options.buttonPropsOverride),
          to: toValue(options.bundleLinkTo),
        };

      default: {
        if (toValue(options.soldOut) && !buttonAtcState.value) {
          return {
            text: '⏰ Remind Me',
            variant: 'white',
            width: 'full-width',
            size,
            style: toValue(options.buttonStyle),
          };
        }
        return {
          text: atcButtonText.value,
          variant: atcButtonVariant.value,
          width: 'full-width',
          style: toValue(options.buttonStyle),
          ...toValue(options.buttonPropsOverride),
          disabled:
            toValue(options.disableCartFunctions) ||
            allowedQuantity.value === 0 ||
            clampedQuantity.value === 0,
          loading: buttonAtcState.value,
          loadingText: buttonLoadingText.value,
          size,
        };
      }
    }
  });

  return {
    mode,
    atcButtonProps,
    buttonAtcState,
    buttonLoadingText,
    handleAddToCart,
    maxAllowed,
    allowedQuantity,
    clampedQuantity,
    resolvedAtcObject,
  };
};
